// src/utils/commentHighlights.js
//
// View-only "Comment Highlights" decorations for the editor. When the View-tab
// toggle is on, every place where a comment was made is tinted so the author
// can see at a glance which text has feedback attached.
//
// Matching strategy: comments created from the editor carry a `quote` string
// (the text the commenter selected). We search the live editor text for that
// quote and wrap each occurrence in a tint span — but only inside the block
// the comment is anchored to (`block_id`). Searching the whole document lit
// up every "the" in the book for a comment on one "the", and painted a
// passage's quote onto any other paragraph that happened to repeat it. A
// comment whose block no longer exists marks nothing; only legacy comments
// with no block_id at all fall back to matching anywhere. The PDF-reader `highlight`
// rect coordinates are page-pixel based and meaningless in this reflowable
// editor, so we deliberately match on text, not geometry.
//
// Safety model (identical philosophy to formattingMarks.js):
//   - Wrappers are tagged with HIGHLIGHT_ATTR so getCleanHTML() unwraps them
//     losslessly — highlights are NEVER persisted or broadcast.
//   - The block holding the caret is skipped, so typing is never disturbed.
//   - Callers must CLEAR highlights before any structural DOM op (pagination
//     splits text nodes), then re-apply afterwards.
//   - Matching is per-text-node; a quote that spans multiple elements simply
//     isn't highlighted (acceptable — no false positives, no corruption).

import { HIGHLIGHT_ATTR } from './editorSerialize';
import { caretOffsetIn, restoreCaretIn } from './caretPreserve';

// contentEditable litters text with non-breaking spaces, and
// Selection.toString() captures them, so a stored quote routinely reads
// "hola en chapter" while the live DOM has ordinary spaces (or the
// reverse). A literal indexOf then never matches and nothing gets highlighted.
//
// Every character here is a fixed-width space substitute, so the replacement is
// strictly 1:1 — normalized indices still address the ORIGINAL string, and no
// offset remapping is needed when slicing. Do NOT add zero-width characters or
// whitespace collapsing to this set; either would change the length and
// silently corrupt every match position.
const SPACE_VARIANTS = /[\u00a0\u2000-\u200a\u202f\u205f\u3000]/g;
const normalizeSpaces = (s) => String(s).replace(SPACE_VARIANTS, ' ');

const KIND = 'comment';
const HL_CLASS = 'rv-comment-highlight';
const CRITICAL_MOD = 'rv-comment-highlight--critical';
const FOCUS_MOD = 'rv-comment-highlight--focus';
// Marks the whole block when the focused comment has no quotable text to
// point at (e.g. it was written without selecting a passage).
const FOCUS_BLOCK_CLASS = 'rv-comment-focus-block';

/**
 * Whether a comment marks a critical revision, which the design paints in red
 * rather than the default gold.
 *
 * Tags are deliberately free text (see comments.py `_clean_tags`) so the
 * taxonomy can grow without a migration, so this matches on substring rather
 * than an exact enum value — "critical revision", "Critical", and "legal:
 * critical" all read as critical.
 *
 * @param {object} c comment
 * @returns {boolean}
 */
/** Display name for a comment's author, matching how the cards label them. */
function commenterName(c) {
  const u = (c && c.user) || {};
  return u.username || u.name || (u.email ? String(u.email).split('@')[0] : 'Someone');
}

/**
 * Native-tooltip text for a highlighted passage: who commented on it.
 * Phrased to mirror the collaborator layer's "Edited by …" so the two
 * annotation layers read as a set.
 * @param {Set<string>} names
 */
function commentLabel(names) {
  const list = Array.from(names);
  if (list.length === 0) return '';
  if (list.length === 1) return `Comment by ${list[0]}`;
  return `Comments by ${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
}

export function isCriticalComment(c) {
  const tags = c && Array.isArray(c.tags) ? c.tags : [];
  return tags.some((t) => typeof t === 'string' && /critical/i.test(t));
}

// Scope for legacy comments saved before anchoring existed (no block_id).
const ANYWHERE = Symbol('anywhere');

// Structural, non-authored regions that must never be decorated.
const SKIP_SEL = '.rv-end-spacer, .rv-page-header, .rv-page-footer';

/**
 * Remove every comment highlight from the live DOM, restoring original content.
 * Idempotent and lossless. Safe to call when nothing is highlighted.
 * @param {Element|null} root editor host
 */
export function clearCommentHighlights(root) {
  if (!root) return;
  root.querySelectorAll(`.${FOCUS_BLOCK_CLASS}`).forEach((el) => {
    el.classList.remove(FOCUS_BLOCK_CLASS);
  });
  root.querySelectorAll(`[${HIGHLIGHT_ATTR}="${KIND}"]`).forEach((span) => {
    const parent = span.parentNode;
    if (!parent) return;
    // Move the real text back out, then drop the wrapper.
    // NOTE: deliberately no normalize() — adjacent text nodes are left as-is so
    // a live Range/caret anchored to a sibling text node keeps its node + offset
    // valid (same guarantee clearFormattingMarks relies on).
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
  });
}

/**
 * (Re)build all comment highlights from scratch. Always clears first, so it is
 * idempotent — repeated calls never accumulate or double-wrap.
 *
 * @param {Element|null} root        editor host
 * @param {Array}        comments    comment objects (each may have `quote`)
 * @param {Element|null} activeBlock block holding the caret; left untouched
 */
export function applyCommentHighlights(root, comments, activeBlock = null, options = {}) {
  if (!root) return;

  const { focusedBlockId = null } = options;

  // Decorating the caret's own block re-nodes the text a live Range points at,
  // so record the caret first and put it back at the end. This used to be
  // handled by refusing to decorate that block at all — which meant the
  // paragraph you were working in was the one place feedback never showed,
  // exactly backwards. collabHighlights already used this technique.
  const caretOffset = activeBlock ? caretOffsetIn(activeBlock) : null;

  clearCommentHighlights(root);

  // Collect unique, non-trivial quote strings per scope: the block-id a
  // comment is anchored to, or ANYWHERE for legacy comments with none.
  //
  // When several comments target the SAME passage, critical wins: a passage
  // carrying a legal risk must not be painted as a mild suggestion just
  // because a second, gentler comment also lands on it.
  const byScope = new Map(); // scope -> Map(quote -> { critical, focused, authors })
  let focusHasQuote = false;

  (comments || []).forEach((c) => {
    const q = c && c.quote ? String(c.quote).trim() : '';
    const isFocused = focusedBlockId && c && c.block_id === focusedBlockId;
    if (q.length < 2) {
      // A focused comment with nothing quotable still needs to point somewhere.
      return;
    }
    let scope = ANYWHERE;
    if (c.block_id) {
      // Its paragraph is gone: there's nothing honest to mark.
      if (!root.querySelector(`[data-block-id="${c.block_id}"]`)) return;
      scope = c.block_id;
    }
    if (isFocused) focusHasQuote = true;
    if (!byScope.has(scope)) byScope.set(scope, new Map());
    const entry = byScope.get(scope).get(q)
      || { critical: false, focused: false, authors: new Set() };
    entry.critical = entry.critical || isCriticalComment(c);
    entry.focused = entry.focused || !!isFocused;
    // Who to name in the tooltip. Several people can comment on one passage, so
    // collect them; de-duplicated because one person often comments twice.
    entry.authors.add(commenterName(c));
    byScope.get(scope).set(q, entry);
  });

  // Fallback for a focused comment with no quote: mark its whole block, so
  // selecting it in the panel always shows you where it lives.
  if (focusedBlockId && !focusHasQuote) {
    const block = root.querySelector(`[data-block-id="${focusedBlockId}"]`);
    if (block) block.classList.add(FOCUS_BLOCK_CLASS);
  }

  const quotesByScope = new Map();
  byScope.forEach((entries, scope) => {
    quotesByScope.set(scope, Array.from(entries.entries()).map(([q, e]) => ({
      text: q,
      // What we actually search for — see normalizeSpaces.
      search: normalizeSpaces(q),
      critical: e.critical,
      focused: e.focused,
      label: commentLabel(e.authors),
    })));
  });

  if (quotesByScope.size === 0) {
    if (caretOffset != null && activeBlock && root.contains(activeBlock)) {
      restoreCaretIn(activeBlock, caretOffset);
    }
    return;
  }

  // Collect candidate text nodes first (never mutate while the walker walks).
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(t) {
      // NOTE: deliberately NOT rejecting whitespace-only nodes. Text is often
      // split across many nodes (see highlightBlock), and the spaces between
      // words can be nodes of their own — dropping them would corrupt the
      // offsets of everything after them.
      if (!t.nodeValue) return NodeFilter.FILTER_REJECT;
      const parent = t.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      // Skip text we've ALREADY wrapped (guards against double-wrapping), but
      // only our own kind. This used to skip `[HIGHLIGHT_ATTR]` unqualified,
      // which also matched the collaborator-edit layer — and since that layer
      // stamps the attribute on the whole block in its fallback mode, any
      // paragraph someone had edited became un-commentable-on: the two View
      // toggles silently cancelled each other out.
      if (parent.closest(`${SKIP_SEL}, [${HIGHLIGHT_ATTR}="${KIND}"]`)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  // Group text nodes by the block that owns them. Matching runs per BLOCK, not
  // per text node: the editor routinely splits a paragraph into one <span> per
  // character (the formatting toolbar stamps font-family/font-size on each), so
  // a per-node search sees only "h", "o", "l", "a" and can never match a phrase.
  // Grouping by block also stops a quote matching across two paragraphs.
  const byBlock = new Map();
  let node;
  while ((node = walker.nextNode())) {
    const parent = node.parentElement;
    // Normally a page-body child. The second selector is a real fallback, not
    // defensive padding: without it, text inside a <span> whose page scaffold
    // is missing would group by the SPAN, putting every character in its own
    // "block" and defeating the whole point of matching across nodes.
    const block =
      parent.closest('.rv-page-body > *') ||
      parent.closest('p, li, h1, h2, h3, h4, h5, h6, blockquote, td') ||
      parent;
    if (!byBlock.has(block)) byBlock.set(block, []);
    byBlock.get(block).push(node);
  }

  const anywhere = quotesByScope.get(ANYWHERE) || [];
  byBlock.forEach((nodes, block) => {
    const id = block.getAttribute?.('data-block-id')
      || block.closest?.('[data-block-id]')?.getAttribute('data-block-id');
    // This block's own quotes first, so on a tie in length they win over a
    // legacy one. Longest first, so a longer quote wins over a shorter one
    // nested inside it (sort is stable).
    const quotes = [...((id && quotesByScope.get(id)) || []), ...anywhere]
      .sort((a, b) => b.search.length - a.search.length);
    if (quotes.length) highlightBlock(nodes, quotes);
  });

  // The focused comment's words may since have been edited out of its
  // paragraph. Mark the paragraph rather than nothing, so selecting a comment
  // always shows where it lives.
  if (focusedBlockId && focusHasQuote) {
    const block = root.querySelector(`[data-block-id="${focusedBlockId}"]`);
    if (block && !block.querySelector(`.${FOCUS_MOD}`)) block.classList.add(FOCUS_BLOCK_CLASS);
  }

  // Wrapping replaced the text nodes the caret lived in; put it back.
  if (caretOffset != null && activeBlock && root.contains(activeBlock)) {
    restoreCaretIn(activeBlock, caretOffset);
  }
}

/**
 * Find and wrap every quote occurrence within ONE block.
 *
 * The block's text is treated as a single string even though it may be spread
 * over many text nodes in many nested elements — which is the normal case here,
 * since the formatting toolbar wraps each character in its own styled <span>.
 * Matches are found against that joined string, then mapped back onto the
 * individual nodes they cover.
 *
 * A match spanning several nodes produces several wrapper spans (one per node
 * it touches) rather than one span across elements, which the DOM can't express
 * without restructuring the user's markup. Visually they're contiguous —
 * `box-decoration-break: clone` in the stylesheet keeps the tint seamless.
 *
 * @param {Text[]} nodes the block's text nodes, in document order
 * @param {Array<{search:string, critical:boolean, focused:boolean}>} quotes longest-first
 */
function highlightBlock(nodes, quotes) {
  // Join the block's text, remembering where each node sits in the result.
  let joined = '';
  const layout = nodes.map((n) => {
    const start = joined.length;
    joined += n.nodeValue;
    return { node: n, start, end: joined.length };
  });

  // Match against a space-normalized copy so an &nbsp; on either side doesn't
  // defeat the search. The substitution is 1:1, so indices found here address
  // `joined` exactly and the slicing below stays correct.
  const haystack = normalizeSpaces(joined);

  // Find all non-overlapping match ranges [start, end) across the whole block.
  const ranges = [];
  for (const q of quotes) {
    let from = 0;
    while (true) {
      const idx = haystack.indexOf(q.search, from);
      if (idx === -1) break;
      const end = idx + q.search.length;
      const overlaps = ranges.some((r) => idx < r.end && end > r.start);
      if (!overlaps) {
        ranges.push({
          start: idx, end, critical: q.critical, focused: q.focused, label: q.label,
        });
      }
      from = end;
    }
  }
  if (ranges.length === 0) return;

  // Project each block-level match onto the nodes it covers.
  const perNode = new Map();
  ranges.forEach((r) => {
    layout.forEach((seg) => {
      const from = Math.max(r.start, seg.start);
      const to = Math.min(r.end, seg.end);
      if (to <= from) return;                       // no overlap with this node
      if (!perNode.has(seg.node)) perNode.set(seg.node, []);
      perNode.get(seg.node).push({
        start: from - seg.start,                    // local to this node
        end: to - seg.start,
        critical: r.critical,
        focused: r.focused,
        label: r.label,
      });
    });
  });

  // Safe to mutate now: each node is replaced independently, and the ranges
  // were all computed before any of them changed.
  perNode.forEach((localRanges, textNode) => wrapRanges(textNode, localRanges));
}

/**
 * Replace one text node with its text, wrapping the given local ranges.
 * @param {Text} textNode
 * @param {Array<{start:number,end:number,critical:boolean,focused:boolean}>} ranges
 */
function wrapRanges(textNode, ranges) {
  const text = textNode.nodeValue;
  ranges.sort((a, b) => a.start - b.start);

  const frag = document.createDocumentFragment();
  let cursor = 0;
  for (const { start, end, critical, focused, label } of ranges) {
    if (start > cursor) {
      frag.appendChild(document.createTextNode(text.slice(cursor, start)));
    }
    const span = document.createElement('span');
    span.setAttribute(HIGHLIGHT_ATTR, KIND);
    // Native tooltip naming the commenter. Safe to persist-check: this title
    // lives on OUR wrapper, and getCleanHTML unwraps the whole span on save, so
    // it can't leak into the document the way the collab block-mode label did.
    if (label) span.title = label;
    // One pass, layered classes — running a second decoration pass for focus
    // would nest wrappers inside wrappers and fight over the same text nodes.
    span.className = [
      HL_CLASS,
      critical ? CRITICAL_MOD : '',
      focused ? FOCUS_MOD : '',
    ].filter(Boolean).join(' ');
    span.appendChild(document.createTextNode(text.slice(start, end)));
    frag.appendChild(span);
    cursor = end;
  }
  if (cursor < text.length) {
    frag.appendChild(document.createTextNode(text.slice(cursor)));
  }

  textNode.replaceWith(frag);
}
